import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, CardMedia, Stack, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useParams } from 'react-router-dom'

import { isCarouselDto } from '@hcr/pwa/api/optimizely'
import { COLORS, ICONS, unit, UnstyledLink, Z_INDEX } from '@hcr/pwa/components'
import { isNotNull, not } from '@hcr/utils'

import { useServiceDetailsQuery } from '../../../hooks'
import type { ServiceDetailsPathParams } from '../../../models'
import { EMPTY_PATH_PARAM, Path } from '../../../models'
import {
  createLink,
  createLinkExternal,
  createServiceDetailsContent,
  isNotUndefinedOrEmptyPathParam,
  isUndefinedOrEmptyPathParam,
  to,
} from '../../../utils'
import {
  ExpandableText,
  LayoutBase,
  LayoutNavigationBack,
  LayoutNavigationBackError404,
  LayoutNavigationBackLoading,
} from '../../common'

const CTA_HEIGHT = unit(19)

const serviceDetailsContent = createServiceDetailsContent({ createLink, slidesMargin: unit(6.5) })

export const ServiceDetails: FC = () => {
  const params = useParams<ServiceDetailsPathParams>()

  const BACK_NAVIGATION_PATH = to([Path.Services, { destinationId: params.destinationId ?? EMPTY_PATH_PARAM }])

  const serviceDetails = useServiceDetailsQuery(
    { destinationId: String(params.destinationId), serviceId: String(params.serviceId) },
    {
      enabled: isNotUndefinedOrEmptyPathParam(params.destinationId) && isNotUndefinedOrEmptyPathParam(params.serviceId),
    }
  )

  if (
    isUndefinedOrEmptyPathParam(params.destinationId) ||
    isUndefinedOrEmptyPathParam(params.serviceId) ||
    serviceDetails.isError
  ) {
    return <LayoutNavigationBackError404 to={BACK_NAVIGATION_PATH} />
  }

  if (serviceDetails.isSuccess) {
    const link = isNotNull(serviceDetails.data.link) ? createLinkExternal(serviceDetails.data.link) : null

    return (
      <>
        <LayoutNavigationBack to={BACK_NAVIGATION_PATH} arrowVariant='outlined'>
          <Container paddingBottom={isNotNull(link) ? CTA_HEIGHT : 0}>
            <CardMedia image={serviceDetails.data.imageUrl} sx={{ height: unit(94) }} />
            <Stack gap={unit(4)} paddingY={unit(9)} paddingX={unit(7)}>
              <Typography variant='headlineL'>{serviceDetails.data.title}</Typography>
              <ExpandableText value={serviceDetails.data.description} />
            </Stack>
            <Stack gap={unit(1.5)} paddingX={unit(1.5)}>
              {serviceDetails.data.content.filter(not(isCarouselDto)).map(serviceDetailsContent)}
            </Stack>
            <Box padding={unit(6.5)}>
              {serviceDetails.data.content.filter(isCarouselDto).map(serviceDetailsContent)}
            </Box>
          </Container>
        </LayoutNavigationBack>
        {isNotNull(link) && (
          <Cta>
            <LayoutBase>
              <Button
                component={UnstyledLink}
                to={link.to}
                endIcon={<FontAwesomeIcon icon={ICONS.farArrowRight} />}
                sx={{ fontSize: unit(4), height: CTA_HEIGHT }}
                variant='contained'
                color='primary'
              >
                {link.label}
              </Button>
            </LayoutBase>
          </Cta>
        )}
      </>
    )
  }

  return <LayoutNavigationBackLoading to={BACK_NAVIGATION_PATH} />
}

const Container = styled(Box)`
  background-color: ${COLORS.white};
  min-height: 100vh;
`

const Cta = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${Z_INDEX.navigation};
`
